import React from 'react'
import { useNavigate } from 'react-router-dom'
import Constants from '../../../app/utils/constants'
import Header from '../../organisms/Header/Header'
import CommonHeroSection from '../../organisms/commonherosection/CommonHeroSection'
import colors from '../../theme/color'
import homeHeroBg from '../../../assets/images/homepage/homepagebg.webp'

import ringLeft from '../../../assets/images/upskill/ring_left.svg'
import ringRight from '../../../assets/images/upskill/ring_right.svg'
import upperBg from '../../../assets/images/upskill/upperbg.svg'
import ellipse from '../../../assets/images/upskill/Ellipse.svg'
import { ReactComponent as StudentLogo } from '../../../assets/images/upskill/student_logo.svg'
import { ReactComponent as PlacementLogo } from '../../../assets/images/upskill/placement_logo.svg'
import { ReactComponent as RatingLogo } from '../../../assets/images/upskill/rating_logo.svg'
import { ReactComponent as PackageLogo } from '../../../assets/images/upskill/package_logo.svg'

const statCardIcons = [
  { Icon: StudentLogo },
  { Icon: PlacementLogo },
  { Icon: RatingLogo },
  { Icon: PackageLogo },
]

const STAT_CARD_SCROLL_STEPS = [
  [0, 74, 148, 222],
  [0, 52, 104, 156],
  [0, 26, 52, 78],
  [0, 0, 0, 0],
]

const STAT_ANIM_DURATION_MS = 3000

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3)

const getCycles = (digitIndex: number): number => (digitIndex % 2 === 0 ? 5 : 3)

const DigitReel = ({
  targetDigit,
  digitIndex,
  progress,
  cellHeight,
}: {
  targetDigit: number
  digitIndex: number
  progress: number
  cellHeight: number
}) => {
  const cycles = getCycles(digitIndex)
  const totalCells = cycles * 10 + targetDigit + 1
  const totalScroll = (cycles * 10 + targetDigit) * cellHeight
  const translateY = -(progress * totalScroll)
  const cells = Array.from({ length: totalCells }, (_, i) => i % 10)

  return (
    <span
      style={{
        display: 'inline-flex',
        flexDirection: 'column',
        height: cellHeight,
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <span
        style={{
          display: 'flex',
          flexDirection: 'column',
          transform: `translateY(${translateY}px)`,
          willChange: 'transform',
        }}
      >
        {cells.map((digit, i) => (
          <span
            key={i}
            style={{
              height: cellHeight,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {digit}
          </span>
        ))}
      </span>
    </span>
  )
}

const AnimatedStatValue = ({
  value,
  progress,
  cellHeight,
}: {
  value: string
  progress: number
  cellHeight: number
}) => {
  if (value === '3,680+') {
    const digits = ['3', '6', '8', '0']
    return (
      <>
        <DigitReel targetDigit={3} digitIndex={0} progress={progress} cellHeight={cellHeight} />
        <span style={{ lineHeight: `${cellHeight}px` }}>,</span>
        {digits.slice(1).map((d, i) => (
          <DigitReel key={i + 1} targetDigit={parseInt(d, 10)} digitIndex={i + 1} progress={progress} cellHeight={cellHeight} />
        ))}
        <span style={{ lineHeight: `${cellHeight}px` }}>+</span>
      </>
    )
  }

  if (value === '92%') {
    return (
      <>
        <DigitReel targetDigit={9} digitIndex={0} progress={progress} cellHeight={cellHeight} />
        <DigitReel targetDigit={2} digitIndex={1} progress={progress} cellHeight={cellHeight} />
        <span style={{ lineHeight: `${cellHeight}px` }}>%</span>
      </>
    )
  }

  if (value === '4.8/5') {
    return (
      <>
        <DigitReel targetDigit={4} digitIndex={0} progress={progress} cellHeight={cellHeight} />
        <span style={{ lineHeight: `${cellHeight}px` }}>.</span>
        <DigitReel targetDigit={8} digitIndex={1} progress={progress} cellHeight={cellHeight} />
        <span style={{ lineHeight: `${cellHeight}px` }}>/5</span>
      </>
    )
  }

  if (value === '₹8-35L') {
    return (
      <>
        <span style={{ lineHeight: `${cellHeight}px` }}>₹</span>
        <DigitReel targetDigit={8} digitIndex={0} progress={progress} cellHeight={cellHeight} />
        <span style={{ lineHeight: `${cellHeight}px` }}>-</span>
        <DigitReel targetDigit={3} digitIndex={1} progress={progress} cellHeight={cellHeight} />
        <DigitReel targetDigit={5} digitIndex={2} progress={progress} cellHeight={cellHeight} />
        <span style={{ lineHeight: `${cellHeight}px` }}>L</span>
      </>
    )
  }

  return <>{value}</>
}

const HeroSection = () => {
  const navigate = useNavigate()
  const { upskillHero } = Constants
  const [hoveredCard, setHoveredCard] = React.useState<string | null>(null)
  const [cardScrollStep, setCardScrollStep] = React.useState(0)
  const [isDesktopLayout, setIsDesktopLayout] = React.useState(false)
  const [statsProgress, setStatsProgress] = React.useState(0)
  const [hasStatsAnimated, setHasStatsAnimated] = React.useState(false)
  const initialScrollYRef = React.useRef<number | null>(null)
  const statsSectionRef = React.useRef<HTMLDivElement | null>(null)
  const STAT_CELL_HEIGHT = 56

  const statCards = upskillHero.stats.map((stat, index) => ({
    ...stat,
    ...statCardIcons[index],
  }))

  const heroMaskGradient = `linear-gradient(to bottom, ${colors.upskillHeroMaskStart} 0%, ${colors.upskillHeroMaskMidA} 42%, ${colors.upskillHeroMaskMidB} 72%, ${colors.upskillHeroMaskEnd} 100%)`

  React.useEffect(() => {
    const updateViewportMode = () => {
      setIsDesktopLayout(window.innerWidth >= 1024)
    }

    updateViewportMode()
    window.addEventListener('resize', updateViewportMode)
    return () => window.removeEventListener('resize', updateViewportMode)
  }, [])

  React.useEffect(() => {
    const sectionEl = statsSectionRef.current
    if (!sectionEl) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || hasStatsAnimated) return
        setHasStatsAnimated(true)

        const startTime = performance.now()
        let rafId = 0

        const tick = (now: number) => {
          const raw = Math.min((now - startTime) / STAT_ANIM_DURATION_MS, 1)
          setStatsProgress(easeOutCubic(raw))
          if (raw < 1) rafId = requestAnimationFrame(tick)
        }

        rafId = requestAnimationFrame(tick)
      },
      { threshold: 0.35 }
    )

    observer.observe(sectionEl)
    return () => observer.disconnect()
  }, [hasStatsAnimated])

  React.useEffect(() => {
    initialScrollYRef.current = window.scrollY

    const updateScrollStep = () => {
      if (initialScrollYRef.current === null) {
        return
      }

      const scrollDelta = Math.max(0, window.scrollY - initialScrollYRef.current)
      const nextStep = Math.min(3, Math.floor((scrollDelta + 40) / 90))
      setCardScrollStep((previousStep) => (previousStep === nextStep ? previousStep : nextStep))
    }

    updateScrollStep()
    window.addEventListener('scroll', updateScrollStep, { passive: true })
    return () => window.removeEventListener('scroll', updateScrollStep)
  }, [])

  return (
    <CommonHeroSection
      backgroundImage={homeHeroBg}
      wrapperClassName="relative isolate min-h-[100vh] w-full overflow-hidden pb-14 text-white lg:min-h-[950px] lg:pb-1"
      contentClassName="relative z-20 w-full"
    >
      <div className="pointer-events-none absolute inset-0 z-10">
        <img
          src={ringLeft}
          alt=""
          className="absolute -left-20 -top-26 h-auto w-[360px] max-w-[24vw] animate-spin object-contain opacity-95 [animation-duration:30s] [transform-origin:center] [will-change:transform]"
        />
        <img
          src={ringRight}
          alt=""
          className="absolute -right-20 -top-26 h-auto w-[360px] max-w-[24vw] animate-spin object-contain opacity-95 [animation-direction:reverse] [animation-duration:30s] [transform-origin:center] [will-change:transform]"
        />
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `linear-gradient(180deg, ${colors.upskillHeroOverlayStart} 0%, ${colors.upskillHeroOverlayMid} 42%, ${colors.upskillHeroOverlayEnd} 100%)`,
          }}
        />
        <img
          src={upperBg}
          alt=""
          className="absolute left-0 top-0 h-[28vh] min-h-[190px] max-h-[320px] w-full object-cover object-top opacity-70 mix-blend-screen"
          style={{ maskImage: heroMaskGradient, WebkitMaskImage: heroMaskGradient }}
        />
        <img
          src={ellipse}
          alt=""
          className="absolute left-1/2 top-[26%] h-auto w-[99vw] max-w-none -translate-x-1/2 -translate-y-1/2  "
        />
      </div>

      <div className="relative z-20">
        <Header />
      </div>

      <div className="relative mx-auto mt-18 flex w-full max-w-[1260px] flex-col items-center px-5 text-center md:px-8 lg:mt-22 lg:px-10">
        <h1 className="max-w-[980px] font-rajdhani text-3xl font-semibold leading-[1.0] md:text-5xl lg:text-6xl xl:text-7xl 2xl:text-8xl">
          {upskillHero.titleLines[0]}
          <br />
          {upskillHero.titleLines[1]}
        </h1>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-4 md:gap-5">
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-full border-2 border-[var(--upskill-btn-border)] bg-transparent px-8 py-3 font-rajdhani font-semibold text-[1.45rem] leading-[1.2] text-white transition-all duration-600 ease-in hover:bg-[var(--upskill-btn-hover)] hover:text-white md:px-10 md:text-[1.6rem]"
            style={{ '--upskill-btn-border': colors.purple, '--upskill-btn-hover': colors.purple } as React.CSSProperties}
            onClick={() => navigate('/contactus')}
          >
            {upskillHero.primaryCta}
          </button>
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-full border-2 border-[var(--upskill-btn-border)] bg-transparent px-8 py-3 font-rajdhani font-semibold text-[1.45rem] leading-[1.2] text-white transition-all duration-600 ease-in hover:bg-[var(--upskill-btn-hover)] hover:text-white md:px-10 md:text-[1.6rem]"
            style={{ '--upskill-btn-border': colors.purple, '--upskill-btn-hover': colors.purple } as React.CSSProperties}
            onClick={() => navigate('/contactus')}
          >
            {upskillHero.secondaryCta}
          </button>
        </div>

        <p className="mt-8 max-w-[980px] font-gilroy-medium text-[1.25rem] leading-[1.45] text-white/100 md:text-[1.7rem]">
          {upskillHero.description}
        </p>

        <div ref={statsSectionRef} className="mt-12 grid w-full max-w-[1120px] grid-cols-2 gap-4 lg:grid-cols-4">
          {statCards.map((card, index) => {
            const isHovered = hoveredCard === card.label
            const translateY = isDesktopLayout ? STAT_CARD_SCROLL_STEPS[cardScrollStep][index] : 0

            return (
              <article
                key={card.label}
                className="rounded-2xl px-3 py-4 md:px-6 md:py-8 text-center backdrop-blur-sm transition-all duration-500 ease-in-out"
                style={{
                  backgroundColor: isHovered ? colors.upskillStatCardHoverSoft : colors.upskillStatCardBase,
                  boxShadow: isHovered ? `0 12px 36px ${colors.upskillStatCardHoverShadow}` : 'none',
                  transform: `translateY(${translateY}px)`,
                } as React.CSSProperties}
                onMouseEnter={() => setHoveredCard(card.label)}
                onMouseLeave={() => setHoveredCard(null)}
              >
                <div
                  className="mx-auto md:mb-5 flex h-14 w-14 items-center justify-center rounded-full transition-colors duration-500 ease-in-out"
                  style={{ backgroundColor: isHovered ? colors.white : colors.upskillStatCardHover }}
                >
                <div className="relative h-7 w-7">
                  <card.Icon
                    aria-hidden
                    className={`absolute inset-0 h-7 w-7 object-contain upskill-stat-icon ${isHovered ? 'is-hovered' : ''}`}
                  />
                </div>
              </div>
              <h3
                className="font-gilroy-bold text-2xl md:text-[2.8rem] leading-none inline-flex items-center justify-center"
                style={{ height: STAT_CELL_HEIGHT }}
              >
                <AnimatedStatValue value={card.value} progress={statsProgress} cellHeight={STAT_CELL_HEIGHT} />
              </h3>
              <p className="md:mt-3 font-gilroy-medium text-xl md:text-[1.55rem] text-white/92">{card.label}</p>
              </article>
            )
          })}
        </div>

        <div className="mt-12 max-w-[980px]">
          <h2 className="font-rajdhani text-[2rem] md:text-[3.4rem] font-bold leading-[1.0]">{upskillHero.pathTitle}</h2>
          <p className="mt-1 pt-5 pb-10 font-gilroy-medium text-[1.4rem] md:text-[1.7rem] leading-[1.35] text-white/100">
            {upskillHero.pathDescription}
          </p>
        </div>
      </div>
    </CommonHeroSection>
  )
}

export default HeroSection
